<template>
  <el-card class="search-card" shadow="never">
    <el-form ref="searchForm" :inline="true" :model="searchBar" size="small" label-width="85px">
      <el-form-item label="订单编号：" prop="orderId">
        <el-input v-model.trim="searchBar.orderId" class="order-input" placeholder="请输入订单编号" clearable @keyup.enter.native="search"></el-input>
      </el-form-item>
      <el-form-item label="所属油站：" prop="gasId">
        <el-select v-model="searchBar.gasId" clearable placeholder="全部">
          <el-option label="全部" value=""></el-option>
          <el-option v-for="item in gasList" :key="item.gasId" :label="item.gasName" :value="item.gasId"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="推广类型：" prop="promotionType">
        <el-select v-model="searchBar.promotionType" clearable placeholder="全部">
          <el-option label="全部" value=""></el-option>
          <el-option
            v-for="item in promotionTypeList"
            :key="item.promotionType || item.typeCode"
            :label="item.promotionTypeName || item.typeName"
            :value="item.promotionType || item.typeCode">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="订单时间：">
        <el-date-picker
          v-model="orderTime"
          type="daterange"
          unlink-panels
          :clearable="false"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          :picker-options="pickerOptions"
          @change="onChangeOrderTime">
        </el-date-picker>
      </el-form-item>
    </el-form>
    <div class="btn-wrap">
      <el-button type="primary" size="mini" @click="search">查询</el-button>
      <el-button size="mini" @click="resetForm">重置</el-button>
      <download-button v-if="exportApi" :downloadApi="exportApi" :queryParams="lastQueryParams"></download-button>
    </div>
  </el-card>
</template>

<script>
import dayjs from 'dayjs'
import { getDistributorResourceList, getPromotionTypeList } from '@/api/promotionManage'

export default {
  name: 'promotionSearchBar',
  props: {
    // 导出接口，不传则不展示导出按钮
    exportApi: Function
  },
  data () {
    return {
      searchBar: {
        orderId: '',       // 订单编号
        gasId: '',         // 所属油站
        promotionType: ''  // 推广类型
      },
      orderTime: [],       // 订单时间范围（默认近31天，最大跨度32天）
      lastQueryParams: {}, // 最近一次查询条件（导出复用）
      gasList: [],         // 场站下拉
      promotionTypeList: [], // 推广类型下拉
      selectDate: '',      // 时间范围选择临时值
      pickerOptions: {
        onPick: ({ maxDate, minDate }) => {
          this.selectDate = minDate.getTime()
          if (maxDate) {
            this.selectDate = ''
          }
        },
        // 需求要求自定义订单时间最多可查32天；Element 的 daterange 用禁用日期做第一层限制。
        disabledDate: (time) => {
          if (this.selectDate !== '') {
            const one = 31 * 24 * 3600 * 1000
            return time.getTime() < this.selectDate - one || time.getTime() > this.selectDate + one
          }
        }
      }
    }
  },
  async created () {
    this.setDefaultTime()
    await this.initOptions()
    // 首次进入按默认条件查询
    this.search()
  },
  methods: {
    isSuccess (code) {
      return code === 0 || code === 200
    },
    // 设置默认查询时间：近31天（含当天）
    setDefaultTime () {
      this.orderTime = [
        dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD')
      ]
    },
    // 时间change校验，picker已限制跨度，此处兜底防止超出32天
    onChangeOrderTime (val) {
      if (!val) {
        this.setDefaultTime()
        return
      }
      if (dayjs(val[1]).valueOf() - dayjs(val[0]).valueOf() > 31 * 24 * 3600 * 1000) {
        this.setDefaultTime()
        this.$message.error('选择时间段不能超过32天')
      }
    },
    // 组装查询条件（不含分页，导出复用）
    // 页面表单字段按前端语义命名，发请求时统一转换为接口文档字段。
    buildSearchParams () {
      const [startTime = '', endTime = ''] = this.orderTime || []
      return {
        orderNo: this.searchBar.orderId,
        gasId: this.searchBar.gasId,
        promotionType: this.searchBar.promotionType,
        beginTime: startTime ? `${startTime} 00:00:00` : '',
        endTime: endTime ? `${endTime} 23:59:59` : ''
      }
    },
    // 查询
    search () {
      const params = this.buildSearchParams()
      this.lastQueryParams = params
      this.$emit('search', params)
    },
    // 重置查询条件
    resetForm () {
      this.searchBar = {
        orderId: '',
        gasId: '',
        promotionType: ''
      }
      this.setDefaultTime()
      this.search()
    },
    // 初始化查询区下拉项；菜单 keep-alive 回来时父页面会主动调用，避免只刷新列表不刷新筛选项。
    async initOptions () {
      await this.getGasList()
      await this.getPromotionTypeList()
    },
    // 场站下拉列表
    async getGasList () {
      try {
        const { code, result } = await getDistributorResourceList()
        if (!this.isSuccess(code)) return
        this.gasList = (result || [])
          .map(item => ({
            ...item,
            gasId: item.gasId || item.stationId || item.id,
            gasName: item.gasName || item.stationName || item.name
          }))
          .filter(item => item.gasId && item.gasName)
      } catch (err) {
        this.gasList = []
      }
    },
    // 推广类型下拉列表
    async getPromotionTypeList () {
      try {
        const { code, result } = await getPromotionTypeList()
        if (!this.isSuccess(code)) return
        this.promotionTypeList = result || []
      } catch (err) {
        this.promotionTypeList = []
      }
    }
  }
}
</script>

<style lang="less" scoped>
.search-card {
  margin-bottom: 10px;

  /deep/ .el-form {
    display: flex;
    flex-wrap: wrap;
  }

  /deep/ .el-form-item {
    margin-right: 24px;
  }

  .order-input {
    width: 200px;
  }

  .el-select {
    width: 180px;
  }

  .btn-wrap {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}
</style>
